import type { TelemetryClient } from '@klareospace/pharos-lib-front-utils';

const SOURCE_WINDOW_ON_ERROR = 'window.onerror';
const SOURCE_UNHANDLED_REJECTION = 'unhandledrejection';

/**
 * Symbol used to mark the window object after global handlers are registered.
 * Using Symbol.for makes it survive module reloads in long-lived dev servers.
 */
const HANDLERS_REGISTERED_KEY = Symbol.for('pharos.telemetry.handlersRegistered');

type WindowWithFlag = typeof window & {
  [HANDLERS_REGISTERED_KEY]?: boolean;
};

/**
 * Register additive window-level error handlers for uncaught errors and
 * unhandled promise rejections.
 *
 * - Additive only: does not call preventDefault, does not override window.onerror.
 * - Idempotent: safe to call multiple times — handlers are registered at most once
 *   per page lifecycle (guarded by a flag on the window object).
 * - Silent: errors thrown by the telemetry client are swallowed.
 */
export function registerGlobalErrorHandlers(client: TelemetryClient): void {
  const win = window as WindowWithFlag;
  if (win[HANDLERS_REGISTERED_KEY]) return;
  win[HANDLERS_REGISTERED_KEY] = true;

  window.addEventListener('error', (event: ErrorEvent): void => {
    try {
      const error = event.error instanceof Error ? event.error : new Error(event.message);
      client.trackException(error, { source: SOURCE_WINDOW_ON_ERROR });
    } catch {
      // Swallowed.
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
    try {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      client.trackException(error, { source: SOURCE_UNHANDLED_REJECTION });
    } catch {
      // Swallowed.
    }
  });
}
